<script>
    import {onMount, onDestroy, tick} from 'svelte';
    import {
        adventCalendarEvent,
        castleLevel,
        luckyDrawEvent,
        rechargeRewardsEvent,
        storePacks,
        storeSection,
        vipLevel,
        subscriptions,
        growthFundConfig,
        monsterGrowthFundConfig,
        claimedGrowthFundPrizes,
        claimedMonsterGrowthFundPrizes,
        battlePassEvent,
        currentAdventClaimed,
        playerGrowthFund,
        playerMonsterGrowthFund,
        defeatedMonsterLevels,
        chainPromoEvent
    } from "../stores.js";
    import {now} from "./battlepass/now.js";
    import {isPrimetimePendingStart, getNextPackTransition} from "../utils.js";
    import {_} from "svelte-i18n";
    import Fa from 'svelte-fa';
    import {faLock} from "@fortawesome/free-solid-svg-icons";
    import Ribbon from "./Ribbon.svelte";
    import NotificationDot from "./NotificationDot.svelte";
    import {deriveTabInterest} from "./tab-interest.js";
    import {adventLabelKey} from "./advent-variant.js";
    import {seenVersion, readSeenFlags} from "./tab-seen.js";


    let tabsList;
    let dragging = false;
    let dragStartX = 0;
    const dragThreshold = 5; // px of movement before entering drag mode
    let rerenderTrigger = $state(0);
    let expirationTimeout = null;

    // Filter out expired and not-yet-started primeTime packs for tab visibility, re-evaluated when rerenderTrigger changes
    let activeStorePacks = $derived.by(() => {
        rerenderTrigger; // Reference triggers reactivity
        if (!$storePacks?.length) return [];
        const now = Date.now();
        return ($storePacks || []).filter(pack =>
            (!pack.endAt || new Date(pack.endAt).getTime() > now) && !isPrimetimePendingStart(pack, now)
        );
    });

    function scheduleTabExpiration() {
        if (expirationTimeout) clearTimeout(expirationTimeout);

        const now = Date.now();
        // Use the raw store so pending primeTime packs (excluded from activeStorePacks) are considered
        const nextTransition = getNextPackTransition($storePacks, now);

        if (nextTransition === null) return;

        // Clamp to the setTimeout max (~24.8 days) — larger delays fire immediately
        const delay = Math.min(nextTransition - now + 1000, 2 ** 31 - 1);

        expirationTimeout = setTimeout(() => {
            rerenderTrigger = Date.now(); // Forces re-evaluation and re-schedules via reactive statement
        }, delay);
    }

    // Schedule when activeStorePacks changes (includes when rerenderTrigger updates)
    $effect(() => {
        if (activeStorePacks.length >= 0) {
            scheduleTabExpiration();
        }
    });

    // Re-run manageIcons when tabs change (activeStorePacks drives tab rendering)
    $effect(() => {
        activeStorePacks; // reactive dependency — re-run when tabs change
        tick().then(() => manageIcons());
    });


    function manageIcons() {
        if (!tabsList) return;

        const leftArrowContainer = tabsList.parentElement.querySelector('.left-arrow');
        const rightArrowContainer = tabsList.parentElement.querySelector('.right-arrow');

        if (tabsList.scrollLeft >= 20) {
            leftArrowContainer.classList.add('active');
        } else {
            leftArrowContainer.classList.remove('active');
        }

        let maxScrollValue = tabsList.scrollWidth - tabsList.clientWidth - 20;

        if (tabsList.scrollLeft >= maxScrollValue) {
            rightArrowContainer.classList.remove('active');
        } else {
            rightArrowContainer.classList.add('active');
        }
    }

    function handleArrowClick(direction) {
        const scrollAmount = 200;
        tabsList.scrollLeft += direction === 'right' ? scrollAmount : -scrollAmount;
    }

    function handleMouseDown(e) {
        dragging = true;
        dragStartX = e.clientX;
    }

    function handleMouseMove(e) {
        if (!dragging) return;
        // Only enter drag mode after moving past threshold
        if (Math.abs(e.clientX - dragStartX) > dragThreshold) {
            tabsList.classList.add('dragging');
        }
        tabsList.scrollLeft -= e.movementX;
    }

    function handleMouseUp() {
        dragging = false;
        tabsList.classList.remove('dragging');
    }

    onMount(() => {
        manageIcons();
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
        };
    });

    onDestroy(() => {
        if (expirationTimeout) clearTimeout(expirationTimeout);
    });

    function shouldDisplayTab(section, packs) {
        // Find the featured pack with lowest position that matches criteria (from active packs only)
        const featuredPacks = packs.filter(p => {
            if (!p.isFeatured) return false;
            // Include promos or goldPacks with primetime
            return p.storeSection === 'promos' ||
                   (p.storeSection === 'goldPacks' && p.specialTitle && p.specialTitle.toLowerCase().includes('primetime'));
        });
        const lowestFeaturedPack = featuredPacks.length > 0
            ? featuredPacks.sort((a, b) => {
                // First sort by position (lowest first)
                const positionDiff = (a.position || 0) - (b.position || 0);
                if (positionDiff !== 0) return positionDiff;

                // If positions are equal, prioritize showPlayerName: true
                if (a.showPlayerName && !b.showPlayerName) return -1;
                if (!a.showPlayerName && b.showPlayerName) return 1;

                return 0;
            })[0]
            : null;

        // Section-specific filtering (packs already excludes expired packs)
        let sectionPacks;
        if (section === 'promos') {
            sectionPacks = packs.filter(pack => pack.storeSection === section && !pack.luckyDraw);
        } else {
            sectionPacks = packs.filter(pack => pack.storeSection === section);
        }

        // If the lowest featured pack is in this section, exclude it from the count
        if (lowestFeaturedPack && lowestFeaturedPack.storeSection === section) {
            sectionPacks = sectionPacks.filter(pack => pack !== lowestFeaturedPack);
        }

        // Display the tab if the section has at least one non-expired pack after excluding the featured pack
        return sectionPacks.length > 0;
    }

    // Reactive derived object that explicitly depends on activeStorePacks
    let visibleTabs = $derived({
        beginnerPacks: shouldDisplayTab('beginnerPacks', activeStorePacks),
        welcomeBackPacks: shouldDisplayTab('welcomeBackPacks', activeStorePacks),
        promos: shouldDisplayTab('promos', activeStorePacks),
        specialEvents: shouldDisplayTab('specialEvents', activeStorePacks) || activeStorePacks.some(p => p.storeSection === 'cardCollection') || activeStorePacks.some(p => p.storeSection === 'pvpArena') || activeStorePacks.some(p => p.storeSection === 'singleJackFever') || !!$chainPromoEvent?.id,
        customizablePacks: shouldDisplayTab('customizablePacks', activeStorePacks),
        dailyDeals: shouldDisplayTab('dailyDeals', activeStorePacks),
        midweekMadness: shouldDisplayTab('midweekMadness', activeStorePacks),
        webshopExclusive: shouldDisplayTab('webshopExclusive', activeStorePacks),
        advancementRewards: (() => {
            const growthLevels = $growthFundConfig?.levels ? Object.keys($growthFundConfig.levels) : [];
            const growthAllClaimed = growthLevels.length > 0 && growthLevels.every(l => $claimedGrowthFundPrizes.includes(parseInt(l)));
            const hasGrowth = growthLevels.length > 0 && !growthAllClaimed;

            const monsterLevels = $monsterGrowthFundConfig?.levels ? Object.keys($monsterGrowthFundConfig.levels) : [];
            const monsterAllClaimed = monsterLevels.length > 0 && monsterLevels.every(l => $claimedMonsterGrowthFundPrizes.includes(parseInt(l)));
            const hasMonster = monsterLevels.length > 0 && !monsterAllClaimed;

            const hasResourceTrove = activeStorePacks.some(p => p.storeSection === 'resourceTrove');

            return hasGrowth || hasMonster || hasResourceTrove;
        })(),
    });

    // NOTE: `$seenVersion` is read deliberately (not dead code). readSeenFlags()
    // reads localStorage non-reactively, so this bare dependency is what makes the
    // map recompute after a sub-tab's markSeenFlag() bumps the version — keep it.
    let interest = $derived(
        ($seenVersion, deriveTabInterest({
            activeStorePacks: activeStorePacks,
            subscriptions: $subscriptions,
            adventCalendarEvent: $adventCalendarEvent,
            currentAdventClaimed: $currentAdventClaimed,
            rechargeRewardsEvent: $rechargeRewardsEvent,
            battlePassEvent: $battlePassEvent,
            growthFundConfig: $growthFundConfig,
            claimedGrowthFundPrizes: $claimedGrowthFundPrizes,
            playerGrowthFund: $playerGrowthFund,
            castleLevel: $castleLevel,
            monsterGrowthFundConfig: $monsterGrowthFundConfig,
            claimedMonsterGrowthFundPrizes: $claimedMonsterGrowthFundPrizes,
            playerMonsterGrowthFund: $playerMonsterGrowthFund,
            defeatedMonsterLevels: $defeatedMonsterLevels,
            chainPromoEvent: $chainPromoEvent,
            seen: readSeenFlags(),
            now: $now,
        }))
    );
</script>

<div class="scrollable-tabs-container">
    <button type="button" class="left-arrow" aria-label="Scroll tabs left" onclick={() => handleArrowClick('left')}>
        <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6 arrow"
        >
            <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15.75 19.5L8.25 12l7.5-7.5"
            />
        </svg>
    </button>
    <!-- svelte-ignore a11y_invalid_attribute -->
    <ul class="tabs is-full" role="tablist" bind:this={tabsList} onscroll={manageIcons} onmousedown={handleMouseDown} onmousemove={handleMouseMove} onkeydown={(e) => { if (e.key === 'ArrowRight') handleArrowClick('right'); else if (e.key === 'ArrowLeft') handleArrowClick('left'); }}>
        {#if Object.keys($adventCalendarEvent).length !== 0}
            <li><a href="#" role="button" class:active={$storeSection === 'adventCalendar'} onclick={(e) => { e.preventDefault(); $storeSection = 'adventCalendar'; }}>
                {$_(adventLabelKey($adventCalendarEvent.uniqueIdentifier))}
                {#if localStorage.getItem("adventCalendarSeen") !== "true"}
                    <Ribbon>{$_('New')}</Ribbon>
                {/if}
                {#if interest.adventCalendar?.dot}<NotificationDot kind={interest.adventCalendar.kind} />{/if}
            </a></li>
        {/if}
        {#if Object.keys($battlePassEvent).length !== 0 && new Date($battlePassEvent.claimBy).getTime() > $now}
            <li><a href="#" role="button" class:active={$storeSection === 'webBattlePass'} onclick={(e) => { e.preventDefault(); $storeSection = 'webBattlePass'; }}>
                {$_('WebBattlePassShopTab', { default: 'Season Pass' })}
                {#if localStorage.getItem("webBattlePassSeen") !== "true"}
                    <Ribbon>{$_('New')}</Ribbon>
                {/if}
                {#if interest.webBattlePass?.dot}<NotificationDot kind={interest.webBattlePass.kind} />{/if}
            </a></li>
        {/if}
        {#if activeStorePacks.length > 0}
            {#if activeStorePacks.filter((pack) => {
                if (!pack.isFeatured) return false;
                // Include promos or goldPacks with primetime
                return pack.storeSection === 'promos' ||
                       (pack.storeSection === 'goldPacks' && pack.specialTitle && pack.specialTitle.toLowerCase().includes('primetime'));
            }).length > 0}
                <li><a href="#" role="button" class:active={$storeSection === 'featured'} onclick={(e) => { e.preventDefault(); $storeSection = 'featured'; }}>{$_('NewShopTabFeatured')}</a></li>
            {/if}
            {#if visibleTabs.beginnerPacks}
                <li><a href="#" role="button" class:active={$storeSection === 'beginnerPacks'} onclick={(e) => { e.preventDefault(); $storeSection = 'beginnerPacks'; }}>{$_('NewShopTabBeginnerDeals')}</a></li>
            {/if}
            {#if visibleTabs.welcomeBackPacks}
                <li><a href="#" role="button" class:active={$storeSection === 'welcomeBackPacks'} onclick={(e) => { e.preventDefault(); $storeSection = 'welcomeBackPacks'; }}>{$_('NewShopTabWelcomeBack')}</a></li>
            {/if}
            {#if visibleTabs.promos}
                <li><a href="#" role="button" class:active={$storeSection === 'promos'} onclick={(e) => { e.preventDefault(); $storeSection = 'promos'; }}>{$_('NewShopTabPromos')}{#if interest.promos?.dot}<NotificationDot kind={interest.promos.kind} />{/if}</a></li>
            {/if}
            {#if visibleTabs.specialEvents}
                <li><a href="#" role="button" class:active={$storeSection === 'specialEvents'} onclick={(e) => { e.preventDefault(); $storeSection = 'specialEvents'; }}>{$_('EventsWindowTitle')}{#if interest.specialEvents?.dot}<NotificationDot kind={interest.specialEvents.kind} />{/if}</a></li>
            {/if}
            {#if visibleTabs.customizablePacks}
                <!-- <li><a class:active={$storeSection === 'customizablePacks'} onclick={() => $storeSection = 'customizablePacks'}>{$_('NewShopTabCustomizablePacks')}</a></li> -->
            {/if}
            {#if visibleTabs.dailyDeals}
                <li><a href="#" role="button" class:active={$storeSection === 'dailyDeals'} onclick={(e) => { e.preventDefault(); $storeSection = 'dailyDeals'; }}>{$_('NewShopTabDailyDeals')}</a></li>
            {/if}
            {#if visibleTabs.advancementRewards}
                <li><a href="#" role="button" class:active={$storeSection === 'advancementRewards'} onclick={(e) => { e.preventDefault(); $storeSection = 'advancementRewards'; }}>{$_('NewShopTabAdvancementRewards')}{#if interest.advancementRewards?.dot}<NotificationDot kind={interest.advancementRewards.kind} />{/if}</a></li>
            {/if}
            {#if visibleTabs.midweekMadness}
                <li><a href="#" role="button" class:active={$storeSection === 'midweekMadness'} onclick={(e) => { e.preventDefault(); $storeSection = 'midweekMadness'; }}>{$_('NewShopTabMidweekMadness')}</a></li>
            {/if}
            {#if visibleTabs.webshopExclusive}
                <li><a href="#" role="button" class:active={$storeSection === 'webshopExclusive'} onclick={(e) => { e.preventDefault(); $storeSection = 'webshopExclusive'; }}>{$_('NewShopTabWebshopExclusive')}</a></li>
            {/if}
            {#if Object.keys($rechargeRewardsEvent).length !== 0}
                <li><a href="#" role="button" class:active={$storeSection === 'goldRush'} onclick={(e) => { e.preventDefault(); $storeSection = 'goldRush'; }}>{$_('NewShopTabEventRecharge')}{#if interest.goldRush?.dot}<NotificationDot kind={interest.goldRush.kind} />{/if}</a></li>
            {/if}
            {#if Object.keys($luckyDrawEvent).length !== 0 && activeStorePacks.filter((pack) => pack.luckyDraw).length > 0}
                <li><a href="#" role="button" class:active={$storeSection === 'luckyDraw'} onclick={(e) => { e.preventDefault(); $storeSection = 'luckyDraw'; }}>
                    {$_('LuckyDraw')}
                </a></li>
            {/if}
            {#if activeStorePacks.filter((pack) => {
                // Check if the pack is in the 'vip' section
                if (pack.storeSection === 'vip') {
                    // Perform the regex match
                    const match = pack.bundleName.match(/lvl(\d+)pack/);
                    // Ensure match is not null and the matched VIP level equals $vipLevel
                    // Note: Ensure $vipLevel is a string for strict equality check or convert both to the same type
                    return match && match[1] <= $vipLevel;
                }
                return false; // Return false if not in 'vip' section or no regex match
            }).length > 0}
                <li><a href="#" role="button" class:active={$storeSection === 'vip'} onclick={(e) => { e.preventDefault(); $storeSection = 'vip'; }}>{$_('NewShopTabVIP')}</a></li>
            {/if}
            <li><a href="#" role="button" class:active={$storeSection === 'goldAndResources'} onclick={(e) => { e.preventDefault(); $storeSection = 'goldAndResources'; }}>{$_('NewShopTabGoldAndResources')}{#if interest.goldAndResources?.dot}<NotificationDot kind={interest.goldAndResources.kind} />{/if}</a></li>
            <li><a href="#" role="button" class:active={$storeSection === 'giftFriend'} onclick={(e) => { e.preventDefault(); $storeSection = 'giftFriend'; }}>
                {#if $castleLevel < 15}
                    <Fa icon={faLock} class="lock-icon" fw primaryColor="#7f584b" size="1x"/>
                {/if}
                {$_('GiftFriend')}
            </a></li>
            <li><a href="#" role="button" class:active={$storeSection === 'redeemCode'} onclick={(e) => { e.preventDefault(); $storeSection = 'redeemCode'; }}>{$_('RedeemCode')}</a></li>
        {:else}
            {#if $subscriptions?.length > 0}
                <li><a href="#" role="button" class:active={$storeSection === 'freeGifts'} onclick={(e) => { e.preventDefault(); $storeSection = 'freeGifts'; }}>{$_('WebshopHomepageBannerHeader')}</a></li>
            {/if}
        {/if}
    </ul>
    <button type="button" class="right-arrow" aria-label="Scroll tabs right" onclick={() => handleArrowClick('right')}>
        <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6 arrow"
        >
            <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M8.25 4.5l7.5 7.5-7.5 7.5"
            />
        </svg>
    </button>
</div>

<style lang="scss">
  :global {
    .scrollable-tabs-container {
      overflow: hidden;
      position: relative;
      height: 50px;
      width: 100%;
      top: 25px;
    }

    .scrollable-tabs-container .arrow {
      width: 24px;
      height: 24px;
      padding: 8px;
      cursor: pointer;
      color: var(--color-lightGrey);
      border-radius: 50%;
      pointer-events: auto;
      background: none !important;
    }

    .scrollable-tabs-container ul {
      width: 100%;
      display: flex;
      overflow-x: scroll;
      overflow-y: hidden;
      -ms-overflow-style: none;
      scrollbar-width: none;
      scroll-behavior: smooth;
    }

    .scrollable-tabs-container ul.dragging a {
      pointer-events: none;
    }

    .scrollable-tabs-container ul.dragging {
      scroll-behavior: auto;
    }

    .scrollable-tabs-container ul::-webkit-scrollbar {
      display: none;
    }

    .scrollable-tabs-container a {
      color: var(--font-color);
      text-decoration: none;
      background: var(--color-darkGrey);
      padding: 4px 24px;
      display: inline-block;
      border-radius: 4px;
      user-select: none;
      white-space: nowrap;
      border-bottom: none;
    }

    .scrollable-tabs-container li {
      list-style: none;
    }

    .scrollable-tabs-container a.active {
      background: var(--color-lightGrey);
      color: var(--color-darkGrey);
    }

    .scrollable-tabs-container .right-arrow,
    .scrollable-tabs-container .left-arrow {
      position: absolute;
      height: 100%;
      width: 100px;
      top: 0;
      display: none;
      align-items: center;
      padding: 0 10px;
      pointer-events: none;
      border: none;
      margin: 0;
      border-radius: 0;
      cursor: pointer;
      transition: none !important;
      opacity: 1 !important;
      transform: none !important;
      box-shadow: none !important;
      background-color: transparent;
    }

    .scrollable-tabs-container .right-arrow.active,
    .scrollable-tabs-container .left-arrow.active {
      display: flex;
      pointer-events: auto;
    }

    .scrollable-tabs-container .left-arrow:hover,
    .scrollable-tabs-container .right-arrow:hover,
    .scrollable-tabs-container .left-arrow:active,
    .scrollable-tabs-container .right-arrow:active {
      opacity: 1 !important;
      transform: none !important;
      box-shadow: none !important;
      background-color: transparent;
    }

    .scrollable-tabs-container .right-arrow {
      right: 0;
      background: linear-gradient(to left, #000 30%, transparent) !important;
      justify-content: flex-end;
      z-index: 1;
    }

    .scrollable-tabs-container .left-arrow {
      background: linear-gradient(to right, #000 30%, transparent) !important;
      z-index: 1;
      left: 0;
    }
  }
</style>